import { Clear, Search } from "@mui/icons-material";
import { Box, Button, IconButton, InputAdornment, TextField } from "@mui/material";
import React from "react";
import { Control, Controller, FieldValues, Path, UseFormHandleSubmit, UseFormResetField } from "react-hook-form";
import useSearchFieldState from "./store";

interface SearchFieldProps<T extends FieldValues> {
    testIdPrefix: string;
    control: Control<T, any>;
    isFetching: boolean;
    resetField: UseFormResetField<T>;
    handleSubmit: UseFormHandleSubmit<T, undefined>;
    onSubmit: (data: T) => void;
}

export const SearchField = <T extends FieldValues>({
    testIdPrefix,
    control,
    isFetching,
    resetField,
    handleSubmit,
    onSubmit
}: SearchFieldProps<T>) => {
    const { setSearchFieldFocused, isSearchFieldFocused } = useSearchFieldState();



    return (
        <Box component="form"
            onSubmit={handleSubmit((data) => {
                onSubmit(data);
                setSearchFieldFocused(false);
            })}
            sx={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
            }}>
            <Controller
                name={"query" as Path<T>}
                control={control}
                render={({ field }) => (
                    <TextField
                        id={`${testIdPrefix}-search-input`}
                        inputProps={{ 'data-testid': `${testIdPrefix}-search-input` }}
                        {...field}
                        fullWidth
                        onFocus={() => setSearchFieldFocused(true)}
                        placeholder="Search"
                        onBlur={(event) => {
                            field.onBlur();
                            setSearchFieldFocused(false);
                        }}
                        sx={{
                            '& .MuiInputBase-root': {
                                height: '44px',
                                borderTopLeftRadius: '12px',
                                borderBottomLeftRadius: '12px',
                                borderBottomRightRadius: '0px',
                                borderTopRightRadius: '0px',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: '1px solid #123257',
                            },
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                border: '1px solid #FF5733',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                border: '1px solid #123257',
                            },
                            '& .MuiInputBase-input': {
                                color: '#6d87aa',
                            },
                        }}
                        disabled={isFetching}
                        variant="outlined"

                        InputProps={{
                            endAdornment: field.value && (
                                <InputAdornment position="end">
                                    <IconButton
                                        data-testid={`${testIdPrefix}-search-clear-button`}
                                        onClick={() => {
                                            resetField("query" as Path<T>);
                                            field.onChange("");

                                             handleSubmit((data) => {
                                                onSubmit(data);
                                                setSearchFieldFocused(false);
                                            })();
                                        }}
                                        edge="end"
                                        disabled={isFetching}
                                    >
                                        <Clear />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
            />

            <Button
                id={`${testIdPrefix}-search-submit-button`}
                data-testid={`${testIdPrefix}-search-submit-button`}
                type="submit"
                variant="contained"
                sx={{
                    width: "54px",
                    height: "44px",
                    borderTopRightRadius: '12px',
                    borderBottomRightRadius: '12px',
                    borderTopLeftRadius: '0px',
                    borderBottomLeftRadius: '0px',
                    backgroundColor: isSearchFieldFocused ? "secondary.main" : "main.primary"
                }}>
                <Search sx={{
                    width: "54px",
                    fontSize: "24px",
                }} />
            </Button>
        </Box>
    );
}